import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService, ResponseObject } from '../../Services/auth.service';
import { NotificationsService } from '../../Services/shared/notifications.service';
import { NotificationTypes } from '../../app.enums';
import { SelectItem } from 'primeng/api';
import { slideDown } from './header.animations';

interface MegaMenuItem {
  label: string;
  icon?: string;
  routerLink?: string[];
  megaMenu?: MegaMenuSection[];
  badge?: number;
  visible: boolean;
  cssClass?: string;
}

interface MegaMenuSection {
  title: string;
  items: MegaMenuLink[];
  featured?: boolean;
  columns?: number;
}

interface MegaMenuLink {
  label: string;
  icon?: string;
  routerLink: string[];
  description?: string;
  badge?: number;
  visible: boolean;
}

@Component({
    selector: 'ds-admin-header',
    templateUrl: './ds-admin-header.component.html',
    styleUrls: ['./ds-admin-header.component.scss'],
    animations: [slideDown],
    standalone: false
})
export class DsAdminHeaderComponent implements OnInit {
  menuItems: MegaMenuItem[] = [];
  activeMenu: string | null = null;
  authUser: any;
  authConfig: any;

  // User dropdown state
  isUserMenuOpen = false;

  // Mobile menu state
  isMobileMenuOpen = false;
  expandedMobileMenu: string | null = null;

  // Notification inputs from parent
  @Input() notifications: any[] = [];
  @Input() countUnreadNotificaion: number = 0;
  @Input() showNotifications: boolean = false;
  @Input() isLoadingNotifications: boolean = false;

  // Admin-specific inputs
  @Input() isSuperAdmin: boolean = false;
  @Input() roles: SelectItem[] = [];
  @Input() selectedRole: string | undefined;
  @Input() selectedEntity: any;
  @Input() isDropdownOpen: boolean = false;
  @Input() dropDownData: SelectItem[] = [];
  @Input() viewingRoleAsAdmin: boolean = false;

  // Output events for parent
  @Output() toggleNotificationsEvent = new EventEmitter<Event>();
  @Output() handleNotificationClickEvent = new EventEmitter<any>();
  @Output() navigateToNotificationsListEvent = new EventEmitter<void>();
  @Output() selectRoleEvent = new EventEmitter<any>();
  @Output() selectIdEvent = new EventEmitter<any>();
  @Output() toggleSidebarEvent = new EventEmitter<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService
  ) {}

  ngOnInit() {
    this.authUser = this.authService.getCurrentUser();
    this.authConfig = this.authService.getAuthConfig();
    this.initializeMenuItems();
  }

  private initializeMenuItems() {
    const isSuperAdmin = this.authService.IsSuperAdmin();

    this.menuItems = [
      // Dashboard - Direct Link
      {
        label: 'Dashboard',
        icon: 'pi pi-home',
        routerLink: ['/ds-admin-dashboard'],
        visible: true
      },

      // Users - Mega Menu
      {
        label: 'Users',
        icon: 'pi pi-users',
        visible: true,
        megaMenu: [
          {
            title: 'Manage',
            items: [
              { label: 'Artists', icon: 'pi pi-palette', routerLink: ['/admin', 'view'], description: 'Manage all artists', visible: true },
              { label: 'Galleries', icon: 'pi pi-building', routerLink: ['/admin', 'view-galleries'], description: 'Manage all galleries', visible: true },
              { label: 'Admins', icon: 'pi pi-shield', routerLink: ['/admins'], description: 'Manage administrators', visible: isSuperAdmin }
            ]
          }
        ]
      },

      // Communication - Mega Menu
      {
        label: 'Communication',
        icon: 'pi pi-envelope',
        visible: true,
        megaMenu: [
          {
            title: 'Email & SMS',
            items: [
              { label: 'Email Events', icon: 'pi pi-bolt', routerLink: ['/admin', 'email-events'], description: 'Email event triggers', visible: true },
              { label: 'Email Templates', icon: 'pi pi-file', routerLink: ['/admin', 'email-templates'], description: 'Email templates', visible: true },
              { label: 'SMS Templates', icon: 'pi pi-mobile', routerLink: ['/admin', 'sms-templates'], description: 'SMS templates', visible: true }
            ]
          },
          {
            title: 'Automation',
            items: [
              { label: 'Event Workflow', icon: 'pi pi-sitemap', routerLink: ['/admin', 'system-events-workflow'], description: 'Automated workflows', visible: true },
              { label: 'Email Logs', icon: 'pi pi-list', routerLink: ['/admin', 'email-track-log'], description: 'Email delivery logs', visible: true }
            ]
          }
        ]
      },

      // Settings - Mega Menu
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        visible: true,
        megaMenu: [
          {
            title: 'Platform',
            items: [
              { label: 'Art Types', icon: 'pi pi-tags', routerLink: ['/admin', 'art-type'], description: 'Manage art categories', visible: true },
              { label: 'Subscription Rules', icon: 'pi pi-sliders-h', routerLink: ['/subscription-rules'], description: 'Plan & pricing rules', visible: true },
              { label: 'System Settings', icon: 'pi pi-wrench', routerLink: ['/system-settings'], description: 'Global configuration', visible: true }
            ]
          },
          {
            title: 'Monitoring',
            items: [
              { label: 'Application Logs', icon: 'pi pi-server', routerLink: ['/admin', 'application-log'], description: 'System activity logs', visible: true }
            ]
          }
        ]
      }
    ];

    // Filter out invisible items
    this.menuItems = this.menuItems.filter(item => item.visible);
    this.menuItems.forEach(item => {
      if (item.megaMenu) {
        item.megaMenu.forEach(section => {
          section.items = section.items.filter(link => link.visible);
        });
        item.megaMenu = item.megaMenu.filter(section => section.items.length > 0);
      }
    });
  }

  toggleMegaMenu(label: string, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.activeMenu = this.activeMenu === label ? null : label;
  }

  openMegaMenu(label: string) {
    this.activeMenu = label;
  }

  closeMegaMenu() {
    this.activeMenu = null;
  }

  isActive(item: MegaMenuItem): boolean {
    if (item.routerLink) {
      return this.router.isActive(item.routerLink.join('/'), false);
    }
    return false;
  }

  isMenuItemActive(item: MegaMenuItem): boolean {
    if (item.routerLink && this.router.isActive(item.routerLink.join('/'), false)) {
      return true;
    }
    if (item.megaMenu) {
      for (const section of item.megaMenu) {
        for (const link of section.items) {
          if (this.router.isActive(link.routerLink.join('/'), false)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // User menu methods
  toggleUserMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isUserMenuOpen = !this.isUserMenuOpen;
    if (this.isUserMenuOpen) {
      this.closeMegaMenu();
    }
  }

  closeUserMenu() {
    this.isUserMenuOpen = false;
  }

  // Mobile menu methods
  toggleMobileMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (this.isMobileMenuOpen) {
      this.closeMegaMenu();
      this.closeUserMenu();
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.expandedMobileMenu = null;
  }

  toggleMobileSubMenu(label: string) {
    this.expandedMobileMenu = this.expandedMobileMenu === label ? null : label;
  }

  logout(event: Event) {
    event.preventDefault();
    this.closeUserMenu();
    this.authService.logout().subscribe((res) => {
      window.location.reload();
    });
  }

  // Switch Account methods
  onSelectRole(event: any) {
    this.selectRoleEvent.emit(event);
  }

  onSelectId(event: any) {
    this.selectIdEvent.emit(event);
  }

  getPlaceholderText(): string {
    return this.selectedRole === 'Artist' ? '- Select Artist -' : '- Select Gallery -';
  }

  get userFullName(): string {
    if (!this.authUser) return '';
    return `${this.authUser.FirstName || ''} ${this.authUser.LastName || ''}`.trim() || 'Admin';
  }

  get planDisplayText(): string {
    if (this.authConfig?.IsSuperAdmin) {
      return 'Super Admin';
    }
    if (this.authConfig?.IsAdministrator) {
      return 'Administrator';
    }
    return '';
  }

  // Notification methods
  onToggleNotifications(event: Event) {
    this.toggleNotificationsEvent.emit(event);
  }

  onHandleNotificationClick(notification: any) {
    this.handleNotificationClickEvent.emit(notification);
  }

  onNavigateToNotificationsList() {
    this.navigateToNotificationsListEvent.emit();
  }
}
